import { useState, useCallback } from 'react';
import { FORM_TYPE_VAR } from '~/app/constants';

/**
 * useInterviewMode
 *
 * Owns the confirmation-dialog state for starting interview mode:
 *   addInterviewModalFlag  — whether the "start interview?" modal is open
 *   addInterviewFlag       — whether interview mode has been initiated via the modal
 *
 * Takes:
 *   calcAPIRef    — ref to calcAPI (for ReturnFormLoad)
 *   formListRef   — ref to formList (Redux prop) for checking whether interview is already started
 *   onInterviewMode — callback that triggers the actual interview mode start (calls calcAPI + formNav)
 */
const useInterviewMode = ({ calcAPIRef, formListRef, onInterviewMode }) => {
  const [addInterviewModalFlag, setAddInterviewModalFlag] = useState(false);
  const [addInterviewFlag, setAddInterviewFlag] = useState(false);

  const hideAddInterviewModal = useCallback(() => {
    if (addInterviewModalFlag) {
      setAddInterviewModalFlag(false);
    }
  }, [addInterviewModalFlag]);

  const addInterviewConfirm = useCallback(async () => {
    if (!addInterviewFlag) {
      setAddInterviewFlag(true);
      onInterviewMode();
    }
    setAddInterviewModalFlag(false);
  }, [addInterviewFlag, onInterviewMode]);

  const addInterviewCancel = useCallback(() => {
    if (addInterviewFlag) {
      setAddInterviewFlag(false);
    }
    setAddInterviewModalFlag(false);
  }, [addInterviewFlag]);

  const showAddInterviewModal = useCallback(() => {
    const formList = formListRef.current ?? [];
    const interviewModeStarted =
      formList.find(formType => formType.var === FORM_TYPE_VAR.INTERVIEW_MODE) !== undefined;

    if (!addInterviewModalFlag && !interviewModeStarted) {
      setAddInterviewModalFlag(true);
    } else {
      onInterviewMode();
    }
  }, [addInterviewModalFlag, formListRef, onInterviewMode]);

  return {
    addInterviewModalFlag,
    addInterviewFlag,
    setAddInterviewFlag,
    addInterviewConfirm,
    addInterviewCancel,
    showAddInterviewModal,
    hideAddInterviewModal,
  };
};

export default useInterviewMode;
